import { useOnboardingCheck } from "@/composables/useOnboardingCheck";
import { isPupil } from "@/stores/user";
import { route } from "@/utils/routes";
import ahoy from "ahoy.js";
import {
  type NavigationGuardReturn,
  type RouteLocationNormalized,
  type RouteLocationRaw,
} from "vue-router";

// The redirect decisions that depend purely on *who the user is* (not on
// navigation side effects). Kept side-effect-free so it can run both inside the
// navigation guard and after an out-of-band user refetch (tab focus / bfcache
// restore), where no navigation fires to trigger the guard. See
// `useRefetchUsers`.
//
// Visitor logout is deliberately NOT here: it is a side effect (full-page
// redirect) and should only run on real navigation, not on a passive refetch.
export function resolveUserRedirect(
  to: Pick<RouteLocationNormalized, "name" | "meta" | "fullPath">,
  userStore: ReturnType<typeof useUserStore>
): RouteLocationRaw | undefined {
  const user = userStore.user;

  // Pupils enrolled in a class must never sit on the generic start page.
  if (
    isPupil(user) &&
    user.schoolClassId &&
    (to.name === "home" || to.name === "tools")
  ) {
    return {
      name: SCHOOL_CLASS_ROUTES.PUPIL.SHOW,
      params: { id: user.schoolClassId },
      query: { token: user.schoolClassToken },
    };
  }

  // Wrong role for the page → send home.
  if (to.meta.requiresTeacher && userStore.isPupil) {
    return { name: "home" };
  }
  if (to.meta.requiresPupil && !userStore.isPupil) {
    return { name: "home" };
  }

  // Auth required but the (refreshed) user is not authenticated → login.
  if (
    to.meta.requiresAuth &&
    !userStore.isAuthenticated &&
    userStore.isLoggedIn &&
    !userStore.isPupil
  ) {
    return {
      name: "login",
      query: { redirectUri: to.fullPath },
    };
  }

  return undefined;
}

export async function routerBeforeEach(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized
): Promise<NavigationGuardReturn> {
  const userStore = useUserStore();
  const { checkOnboarding } = useOnboardingCheck();
  const { logout } = useSession();

  try {
    const onboardingRedirect = checkOnboarding(to);
    if (onboardingRedirect) {
      return onboardingRedirect;
    }
  } catch (error) {
    console.error("Error during onboarding check:", error);
  }

  if (userStore.isVisitor && to.name === "home") {
    await logout(false);
  }

  // Project routes carry it as a param; a link into a shared item carries it as
  // a query, which is the only context a session-transferred browser has — it
  // starts with an empty localStorage, so without this the pupil's next
  // activity is recorded against no project at all.
  const projectId =
    (to.params as { projectId?: string }).projectId ??
    (to.query as { projectId?: string }).projectId;
  if (isPupil(userStore.user) && projectId) {
    userStore.saveProjectId(projectId);
  }

  // Role/auth-based redirects (pupil start page, requiresTeacher/Pupil/Auth)
  const userRedirect = resolveUserRedirect(to, userStore);
  if (userRedirect) {
    return userRedirect;
  }

  // Analytics — internal tooling opts out via `meta.skipAnalytics` so it does
  // not pollute product page-view data.
  if (!to.meta.skipAnalytics) {
    ahoy.trackView();
  }

  // Enable galleryMode if URL contains public_token
  const galleryModeStore = useIsGalleryModeStore();
  if (to.query.public_token) {
    galleryModeStore.updateGalleryMode(true);
  } else {
    galleryModeStore.updateGalleryMode(false);
  }

  if (userStore.isAuthenticated) {
    // Track daily visit
    const dailyVisitStore = useDailyVisitStore();
    await dailyVisitStore.trackDailyVisit();
  }

  if (to.name === route("course_bookmarks")) {
    return {
      name: route("course_enrollments"),
      params: to.params,
      query: { ...to.query, tab: "bookmarked" },
    };
  }
}
